import * as yup from 'yup';

import dayjs from 'dayjs';

const stringValidationWithRequired = (message: string) => yup.string().required(message).trim();

const stringValidationWithRequiredForName = (message: string) =>
  yup
    .string()
    .required(message)
    .test(
      'no-leading-trailing-space',
      'Do not start or end with space',
      (value) => value === value?.trim()
    )
    .matches(
      /^[a-zA-Z][a-zA-Z0-9 ',&.-]*$/,
      'Must start with Alphabet and can include digits, apostrophe, comma, dash, dot'
    );

const stringValidationWithRequiredForOfficeName = (message: string) =>
  yup
    .string()
    .required(message)
    .trim('Do not start or end  with Space')
    .test({
      name: 'stringValidationWithRequiredForOfficeName',
      message:
        'Must be start with Alphabet and You can use Digits, Apostrophe, Comma, Dash And Dot',
      test: (value) => {
        return /^[a-zA-Z][a-zA-Z0-9',&.\- ]*$/.test(value);
      },
    });
const allowNonEmptyString = (message: string) =>
  yup
    .string()
    .trim()
    .required(message)
    .test(
      'not-empty',
      'Field cannot be empty or just spaces',
      (value) => !!value && value.trim().length > 0
    );

const email = yup
  .string()
  .email('Please Enter Valid Email Address')
  .required('Please Enter Your Email')
  .trim()
  // eslint-disable-next-line max-len, no-useless-escape
  .matches(
    // eslint-disable-next-line max-len, no-useless-escape
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    'Please Enter Valid Email Address'
  );

const faxFormat = yup
  .string()
  .nullable()
  .optional()
  .test({
    name: 'faxFormat',
    message: 'Fax Number must be 10 digits or empty',
    test: (value) => {
      // Allow undefined, null, or empty string (due to .optional())
      if (!value) return true;
      // Remove all non-digit characters
      const digits = value.replace(/\D/g, '');
      return digits.length === 1 || (digits.length === 11 && digits.startsWith('1'));
    },
  });

const url = yup
  .string()
  .nullable()
  .optional()
  .test({
    name: 'urlvalidation',
    message: 'Please enter a valid URL',
    test: (value) => {
      if (!value || value.trim() === '') {
        return true;
      }
      return /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/.test(value);
    },
  });

const mobileNumber = yup.string().test({
  name: 'mobileNumber',
  message: 'Phone Number Must Be 10 Digits',
  test: (value) => {
    // Allow undefined, null, or empty string (due to .optional())
    if (!value) return false;

    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Check if digits form a valid US phone number (11 digits starting with 1)
    return digits.length === 11 && digits.startsWith('1');
  },
});
const password = yup
  .string()
  .required('Please Enter Password')
  .trim()
  .matches(
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/,
    'Please choose a stronger password to protect your account and sensitive health information.'
  );
const confirmPassword = yup
  .string()
  .required('Please Enter Confirm Password')
  .trim()
  .oneOf([yup.ref('password')], 'Password and Confirm Password should match');
const notification = yup.boolean().oneOf([true], '');
const zipcode = yup
  .string()
  .required('Please Enter ZIP Code')
  .trim()
  .matches(/^\d+$/, 'ZIP Code must be in Digits')
  .length(5);

const lengthCheck = /^[A-Za-z\d@$!%*}.<,>?/;:'"+=_)(^#`~|\\{?&-[\]]{8,}/;
const lowerCaseCheck = /^(?=.*[a-z])/;
const UpperCaseCheck = /^(?=.*[A-Z])/;
const digitCheck = /^(?=.*\d)/;
const specialCharCheck = /[!@#$%&*;:',.></_+=}[|\\)(\]{?]/;
const expirydate = yup
  .string()
  .required('Expiry Date is Required')
  .matches(/^((\d{4})|\d{2})-?(0[1-9]|1[0-2]$)/, 'Invalid Expiry Date')
  .test('expiryDate', 'Card has Expired', function (value) {
    if (!value) return false;

    // Extracting month and year from the expiry date
    const [enteredMonth, enteredYear] = value.split('/');

    // Creating a Date object for the current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // Month is zero-based
    const currentYear = currentDate.getFullYear();

    // Comparing the expiry date with the current date
    if (
      +enteredYear < currentYear ||
      (+enteredYear === currentYear && +enteredMonth < currentMonth)
    ) {
      return false; // Card has expired
    }

    return true; // Card is valid
  });

export const checkPasswordCriteria = (value: any) => {
  const errors = [
    {
      name: 'lengthCheck',
      schema: lengthCheck,
      message: '8 characters minimum',
    },
    {
      name: 'lowerCaseCheck',
      schema: lowerCaseCheck,
      message: '1 lowercase characters',
    },
    {
      name: 'upperCaseCheck',
      schema: UpperCaseCheck,
      message: '1 uppercase character',
    },
    {
      name: 'specialCharCheck',
      schema: specialCharCheck,
      message: '1 special character',
    },
    { name: 'digitCheck', schema: digitCheck, message: '1 number' },
  ];
  return errors.map((condition) => {
    return {
      isValid: condition.schema.test(value),
      message: condition.message,
      name: condition.name,
    };
  });
};

export const singupSchema = yup
  .object()
  .shape({
    firstName: stringValidationWithRequiredForName('Please Enter First Name'),
    lastName: stringValidationWithRequiredForName('Please Enter Last Name'),
    email: email,
    mobileNumber: mobileNumber,
    password: password,
    confirmPassword: confirmPassword,
    enableNotification: notification,
    enableSMSNotification: notification,
    servicesAcknowledgement: notification,
    referral: yup.string().required('Please select referral'),
    referralComment: yup.string().when('referral', {
      is: (value: string) => value === 'Other',
      then: (schema) => schema.required('Please add other referral details'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  })
  .strict();
export const transferSingupSchema = yup
  .object()
  .shape({
    firstName: yup.string().optional(),
    lastName: yup.string().optional(),
    email: email,
    mobileNumber: mobileNumber,
    password: password,
    confirmPassword: confirmPassword,
    enableNotification: notification,
    enableSMSNotification: notification,
    servicesAcknowledgement: notification,
  })
  .strict();

export const loginSchema = yup
  .object()
  .shape({
    email: email,
    password: stringValidationWithRequired('Please Enter Password'),
  })
  .strict();

export const forgotPasswordSchema = yup
  .object()
  .shape({
    email: email,
  })
  .strict();

export const resetPasswordSchema = yup
  .object()
  .shape({
    email: email,
    password: password,
    confirmPassword: confirmPassword,
  })
  .strict();

export const verifyForgotPasswordLink = yup.object().shape({
  token: yup.string(),
});

export const querySchema = yup.object().shape({
  message: stringValidationWithRequired('Please Enter Your Queries'),
});

export const zipCodeSchema = yup
  .object()
  .shape({
    zipCode: zipcode,
  })
  .strict();

export const providerDetailsSchema = yup
  .object()
  .shape({
    officeName: allowNonEmptyString('Please Enter Practice or Organization Name'),
    firstName: stringValidationWithRequiredForName('Please Enter PCP First Name'),
    lastName: stringValidationWithRequiredForName('Please Enter PCP Last Name'),
    officePhoneNumber: mobileNumber,
    officeFaxNumber: faxFormat,
    counselorOrTherapist: yup.string(),
  })
  .strict();

export const counselorDetailsSchema = yup
  .object()
  .shape({
    counselorOrTherapist: yup.string().required('Please select Yes or No'),
    officeName: yup.string().when('counselorOrTherapist', {
      is: 'yes',
      then: (schema) => allowNonEmptyString('Please Enter Practice or Organization Name'),
      otherwise: (schema) => schema.notRequired(),
    }),
    firstName: yup.string().when('counselorOrTherapist', {
      is: 'yes',
      then: () => stringValidationWithRequiredForName('Please Enter Counselor First Name'),
      otherwise: (schema) => schema.notRequired(),
    }),
    lastName: yup.string().when('counselorOrTherapist', {
      is: 'yes',
      then: () => stringValidationWithRequiredForName('Please Enter Counselor Last Name'),
      otherwise: (schema) => schema.notRequired(),
    }),
    officePhoneNumber: yup.string().when('counselorOrTherapist', {
      is: 'yes',
      then: () => mobileNumber,
      otherwise: (schema) => schema.notRequired(),
    }),
    officeFaxNumber: faxFormat,
  })
  .strict();

export const patientInfoSchema = yup
  .object()
  .shape({
    id: yup.number(),
    firstName: stringValidationWithRequiredForName('Please Enter Patient First Name'),
    lastName: stringValidationWithRequiredForName('Please Enter Patient Last Name'),
    preferredName: yup.string().optional(),
    dateOfBirth: yup.string(),
    biologicalGender: stringValidationWithRequired('Please select Patient Biological Gender'),
    preferredGender: yup.string().optional(),
    childAddress: yup.string().required('Please Enter Patient Address'),
    zipcode: yup
      .string()
      .required('Please enter valid ZIP Code ')
      .matches(/^\d+$/, 'ZIP Code must be in Digits')
      .test({
        name: 'max',
        message: 'ZIP Code must be 5 Digits',
        test: (value) =>
          value === '1' ||
          value === '' ||
          value?.length === 5 ||
          value === undefined ||
          value === null,
      }),
    city: yup.string(),
    state: yup.string(),
    isActiveCustody: yup.string(),
    preferredLocation: yup
      .string()
      .notOneOf(['preferredLocation'], 'Please select Preferred Location')
      .required('Please select Preferred Location'),
  })
  .strict();

export const pharmacyDetailsSchema = yup
  .object()
  .shape({
    pharmacyName: stringValidationWithRequiredForName('Please Enter Pharmacy Name'),
    phoneNumber: mobileNumber,
    address: yup
      .string()
      .required('Please enter Pharmacy Address')
      .trim('Do not start or end  with Space'),
    zipCode: yup.string().matches(/^\d{5}$/i, 'ZIP Code must be in Digits'),
    accessMedicationHistory: yup.string().required('Please select option'),
  })
  .strict();

export const insuranceDetailsSchema = yup
  .object()
  .shape({
    insuranceCarrier: yup.string().required('Please Select Insurance Carrier Name'),
    planId: stringValidationWithRequired('Please Enter Member Id'),
    groupNo: stringValidationWithRequired('Please Enter Group Number'),
    subscriberName: stringValidationWithRequiredForName('Please Enter Name Of Subscriber'),
    subscriberDob: yup.string().required('Subscriber DOB is required'),
    patientRelation: stringValidationWithRequired('Please Enter Patient Relation'),
    cardImage: yup.string(),
  })
  .strict();

export const creditCardSchema = yup.object().shape({
  cardno: yup.string().max(16).required('Please Enter Credit Card Number'),
  cardholdername: stringValidationWithRequiredForName('Please Enter Card Holder Name'),
  expirydate: expirydate,
  cvv: yup
    .string()
    .required('CVV Number is Required')
    .matches(/^\d{3,4}$/, 'Invalid CVV Number'),
});

export const guardianSchema = yup
  .object()
  .shape({
    id: yup.number(),
    firstName: stringValidationWithRequiredForName('Please Enter First Name'),
    lastName: stringValidationWithRequiredForName('Please Enter Last Name'),
    email: email,
    mobileNumber: mobileNumber,
    relation: yup
      .string()
      .oneOf(['Father', 'Mother', 'Guardian', ''])
      .required('Please Select your Relation to the Patient'),
    guardianCategory: yup.string(),
    other: yup.string().when('relation', ([relation], schema): any => {
      return relation === 'other' ? schema.required('Please Enter Add Details') : schema;
    }),
  })
  .strict();

const medicationSchema = yup.object().shape({
  medication: yup.string(),
  // dispense: yup.string().matches(digitCheck, 'Please enter digits only').required('Please enter dispense'),
  // rxnorm: yup.string().required('Please enter Rxnorm'),
  sig: yup.string(),
});

const diagnosisSchema = yup.object().shape({
  diagnosis: yup.string(),
  diagnosedDate: yup.string(),
});

export const afterMeetingSchema = yup.object().shape({
  treatmentPlan: yup.string().required('Please Select Treatment Plan'),
  pendingBalance: yup.string(),
  others: yup.string().optional(),
  medications: yup.array().of(medicationSchema),
  diagnosis: yup.array().of(diagnosisSchema),
  referrals: yup.string(),
  ratingScales: yup.string(),
  roiForms: yup.string(),
  followup: yup.string(),
});

export const detailsSchema = yup.object().shape({
  chartId: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9]+$/, 'Looks like invalid id'),
  pricingPlan: yup.string(),
  providerId: yup.string(),
  creditCardAvailable: yup.string(),
  creditCardStatus: yup.string(),
  billingStatus: yup.string(),
  insCardStatus: yup.string(),
  coPayAmount: yup
    .string()
    .matches(
      /^\d*[.]{0,1}\d*$/,
      'Please enter Amount in digits. Special characters are not allowed, except for a decimal point.'
    )
    .test('is-decimal', 'Amount must be less than 5000 & greater than 0.50', (value) => {
      if (!value) return true;
      else if (parseInt(value) > 5000 || parseFloat(value) < 0.5) return false;
      return !isNaN(parseFloat(value));
    }),
});

export const upcomingAppointmentSchema = yup.object().shape({
  scheduledDateTime: yup.string(),
});

export const mcatIScoreSchema = yup.object().shape({
  mcatIScore: yup
    .string()
    .trim()
    .required('Please Enter i-Wise Score')
    .matches(/^\d*[.]{0,1}\d*$/, 'i-Wise Score must be in Digits and Only One Dot can Used'),
  updatedOn: yup.string(),
});
export const appointmentSchema = yup
  .object()
  .shape({
    preferredTime: yup
      .object()
      .shape({
        morning: yup.array().of(yup.string()),
        afternoon: yup.array().of(yup.string()),
        evening: yup.array().of(yup.string()),
      })
      .required('Please Select Preferred Time.'),
    contactMethod: yup.string().required('Field Must Be Checked'),
    mobileNumber: mobileNumber,
    consultationType: yup.string().required('Field Must Be Checked'),
    remarks: yup.string(),
  })
  .strict();

export const secondaryGuardianSchema = yup
  .object()
  .shape({
    id: yup.number(),

    firstName: yup.string().when('isEmergency', {
      is: 'yes',
      then: (schema) => schema.required('First Name is required'),
      otherwise: (schema) => schema.optional(),
    }),

    lastName: yup.string().when('isEmergency', {
      is: 'yes',
      then: (schema) => schema.required('Last Name is required'),
      otherwise: (schema) => schema.optional(),
    }),

    email: yup
      .string()
      .email('Invalid Email Format')
      .when('isEmergency', {
        is: 'yes',
        then: (schema) => schema.required('Email is required'),
        otherwise: (schema) => schema.optional(),
      }),

    mobileNumber: yup
      .string()
      .nullable()
      .when('isEmergency', {
        is: 'yes',
        then: (schema) =>
          schema
            .required('Phone Number is required')
            .test({
              name: 'PhoneFormat',
              message: 'Phone Number must be 10 digits',
              test: (value) => {
                if (!value) return false;

                const digits = value.replace(/\D/g, '');

                return (
                  digits.length === 11 && digits.startsWith('1')
                );
              },
            }),
        otherwise: (schema) =>
          schema.optional().test({
            name: 'PhoneFormat',
            message: 'Phone Number must be 10 digits or empty',
            test: (value) => {
              if (!value) return true;

              const digits = value.replace(/\D/g, '');

              return (
                digits.length === 1 ||
                (digits.length === 11 && digits.startsWith('1'))
              );
            },
          }),
      }),

    relation: yup.string().when('isEmergency', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Please select relation to the child')
          .test(
            'valid-relation',
            'Please select relation to the child',
            (value) => value !== 'select_relation_to_the_child'
          ),
      otherwise: (schema) => schema.optional(),
    }),
    guardianCategory: yup.string(),
    other: yup.string(),
  })
  .strict();

export const emargencyGuardianSchema = yup
  .object()
  .shape({
    id: yup.number(),
    contactSource: yup.string().required('Please select contact'),
    firstName: stringValidationWithRequiredForName('Please enter First Name'),
    lastName: stringValidationWithRequiredForName('Please enter Last Name'),
    mobileNumber: yup
      .string()
      .required('Please enter Phone Number')
      .min(11, 'Invalid phone number'),
    relation: yup
      .string()
      .transform((value) => (isNaN(value) ? '' : value))
      .required('Please enter relation to the child'),
    isEmergency: yup.string().required(),
    guardianCategory: yup.string(),
    other: yup.string().when('relation', ([relation], schema): any => {
      return relation === 'other' ? schema.required('Please enter Add Details') : schema;
    }),
  })
  .strict();

export const vitalSchema = yup.object().shape({
  height: yup
    .string()
    .nullable()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      'Please enter Height in digits, with up to 2 decimal places.'
    )
    .test('is-decimal', 'Height Must Be a Number', (value) => {
      if (!value) return true;
      return !isNaN(parseFloat(value));
    }),
  weight: yup
    .string()
    .nullable()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      'Please enter Weight in digits, with up to 2 decimal places.'
    )
    .test('is-decimal', 'Weight Must Be a Number', (value) => {
      if (!value) return true;
      return !isNaN(parseFloat(value));
    }),
  bloodPressure1: yup
    .string()
    .nullable()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      'Please enter Systolic Blood Pressure in digits, with up to 2 decimal places.'
    )
    .test('is-number', 'Blood Pressure Must Be a Number', (value) => {
      if (!value) return true;
      return !isNaN(parseFloat(value));
    }),
  bloodPressure2: yup
    .string()
    .nullable()
    .matches(
      /^\d*(\.\d{1,2})?$/,
      'Please enter Diastolic Blood Pressure in digits, with up to 2 decimal places.'
    )
    .test('is-number', 'Blood Pressure Must Be a Number', (value) => {
      if (!value) return true;
      return !isNaN(parseFloat(value));
    }),
});

export const taskSchema = yup.object().shape({
  taskTitle: yup.string().required('Please enter Task Title'),
  assignedTo: yup.string().required('Please select VMA '),
  category: yup.string(),
  status: yup.string().required('Please select status'),
  dueDate: yup.string(),
  priority: yup.string().required('Please select priority'),
  patientName: yup.string(),
  notes: yup.string(),
});

export const newOffice = yup.object().shape({
  id: yup.number(),
  // Always required
  officeName: stringValidationWithRequiredForName('Enter valid name'),
  officeType: yup.string().notOneOf([''], 'Please select type').required('Please select type'),
  officePhoneNumber: mobileNumber,
  status: yup.string().notOneOf([''], 'Please select status').required('Please select status'),
  // Optional always
  businessEmail: yup.string().notRequired().nullable().email('Invalid email format'),
  officeFaxNumber: faxFormat,
  websiteUrl: url,
  // Required only for Approved / In-Pipeline
  region: yup.string().when('status', {
    is: (val: string) => val === 'Approved' || val === 'In-Pipeline',
    then: (schema) =>
      schema.notOneOf([''], 'Please select region').required('Please select region'),
    otherwise: (schema) => schema.notRequired(),
  }),

  state: yup.string().when('status', {
    is: (val: string) => val === 'Approved' || val === 'In-Pipeline',
    then: (schema) => schema.notOneOf([''], 'Please select state').required('Please select state'),
    otherwise: (schema) => schema.notRequired(),
  }),

  city: yup.string().when('status', {
    is: (val: string) => val === 'Approved' || val === 'In-Pipeline',
    then: (schema) => schema.required('Please select city'),
    otherwise: (schema) => schema.notRequired(),
  }),

  zipcode: yup.string().when('status', {
    is: (val: string) => val === 'Approved' || val === 'In-Pipeline',
    then: (schema) => zipcode.required('Please Enter ZIP Code'),
    otherwise: (schema) => schema.notRequired(),
  }),

  address: yup.string().when('status', {
    is: (val: string) => val === 'Approved' || val === 'In-Pipeline',
    then: (schema) => schema.required('Please select street address'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const chargePaymentSchema = yup.object().shape({
  amount: yup
    .string()
    .matches(
      /^\d*[.]{0,1}\d*$/,
      'Please enter Amount in digits. Special characters are not allowed, except for a decimal point.'
    )
    .test('is-decimal', 'Amount must be less than 5000 & greater than 0.50', (value) => {
      if (!value) return true;
      else if (parseInt(value) > 5000 || parseFloat(value) < 0.5) return false;
      return !isNaN(parseFloat(value));
    }),
  description: yup.string().required('Please enter Description.'),
});

export const changePasswordSchema = yup.object().shape({
  password: password,
  confirmPassword: confirmPassword,
});

export const userManagementSchema = yup.object().shape({
  firstName: stringValidationWithRequiredForName('Please enter First Name.'),
  lastName: stringValidationWithRequiredForName('Please enter Last Name.'),
  email,
  mobileNumber,
  position: yup.string(),
  roleType: yup.string().required('Please select role.'),
  providerId: yup
    .string()
    .trim()
    .transform((value) => (value === '' ? undefined : value))
    .notRequired(),
  /**
   * MM-780: a provider account (Provider/Admin role with a Provider ID entered) must have a
   * Zoom link, since appointment video links come from it. Other users can leave it empty.
   */
  zoomLink: yup
    .string()
    .trim()
    .transform((value) => (value === '' ? undefined : value))
    .url('Enter a valid link URL')
    .when(['roleType', 'providerId'], {
      is: (roleType: string, providerId: string) =>
        ['provider', 'admin'].includes(roleType?.toLowerCase()) && !!providerId,
      then: (schema) => schema.required('Zoom link is mandatory for provider accounts.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  description: yup
    .string()
    .trim()
    .transform((value) => (value === '' ? undefined : value))
    .notRequired(),
  credentials: yup
    .string()
    .trim()
    .transform((value) => (value === '' ? undefined : value))
    .notRequired(),
  profilePicture: yup
    .string()
    .trim()
    .transform((value) => (value === '' ? undefined : value))
    .notRequired(),
});

export const userOfficeManagementSchema = yup.object().shape({
  roleType: yup.string().required('Please select role.'),
  officeLocation: yup.string().required('Please select office location.'),
  user: yup.string().required('Please select user.'),
  providerStartTime: yup.string().when('roleType', {
    is: (val: string) => val?.toLowerCase() === 'provider',
    then: (schema) => schema.required('Start time is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  providerDuration: yup.string().when('roleType', {
    is: (val: string) => val?.toLowerCase() === 'provider',
    then: (schema) => schema.required('Duration is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  providerEndTime: yup.string().when('roleType', {
    is: (val: string) => val?.toLowerCase() === 'provider',
    then: (schema) => schema.required('End time is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const roleManagementSchema = yup.object({
  roleName: yup.string().trim().required('Role name is required'),

  description: yup.string().trim().default(''),

  defaultLanding: yup.string().default(''),
});

export const editAppointmentSchema = yup.object({
  provider: yup.string().required('Provider is required'),

  patient: yup.string().required('Patient is required'),

  status: yup.string().required('Status is required'),

  examRoom: yup.string().required('Exam room is required'),

  office: yup.string().required('Office is required'),

  profile: yup.string().required('Appointment Template is required'),

  time: yup
    .mixed()
    .required('Date & Time is required')
    .test('not-in-past', 'Cannot select past date/time', (value) => {
      if (!value) return false;
      return dayjs(value as string | Date).isAfter(dayjs());
    }),

  duration: yup.number().min(1, 'Duration must be at least 1 minute').required(),

  reason: yup.string().optional(),
  color: yup.string().optional(),
});
